import { localToday } from '../../lib/flightFilters';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  addDoc,
  collection,
  doc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { Check, ChevronLeft, ChevronRight, Download, Sparkles, ShieldCheck, Phone, ExternalLink, LogOut, FileText } from 'lucide-react';
import { db, storage } from '../../lib/firebase';
import { useAuth } from '../../lib/auth';
import { Logo } from '../../components/Logo';
import { Spinner } from '../../components/Spinner';
import { PhotoCapture, type CapturedPhoto } from '../../components/PhotoCapture';
import { sendAlert } from '../../lib/alerts';
import { fmtUSD } from '../../lib/billingCycles';
import { formatPhone, PHONE_MASK } from '../../lib/format';
import { SignaturePad, type SignatureResult } from '../../components/SignaturePad';
import { renderLease, renderAgreementForPlan, sha256, LEASE_VERSION } from '../../lib/lease';
import { downloadAgreementPdf } from '../../lib/agreementPdf';
import { LeaseDocument } from '../../components/LeaseDocument';
import { checkEligibility, isStudentPilot } from '../../lib/eligibility';
import { PaymentCardStep, useCardReturn } from '../../components/PaymentCardStep';
import { formatAddress } from '../../lib/address';
import { DateField } from '../../components/DateField';
import {
  extractDocument,
  type ExtractableDocType,
  type ExtractResult,
} from '../../lib/extract';
import type {
  Disclosures,
  DocumentType,
  EmergencyContact,
  InsuranceInfo,
  PersonalInfo,
  PilotInfo,
} from '../../lib/types';
import { LEASE_DURATION_LABEL } from '../../lib/types';
import { OWNER_LEGAL } from '../../lib/identity';

import { TRACKED_REG } from '../../lib/flightTrack';
const RATING_OPTIONS = [
  'Airplane Single-Engine Land',
  'Airplane Multi-Engine Land',
  'Instrument Airplane',
  'Tailwheel',
  'High Performance',
  'Complex',
];

const DISCLOSURE_LABELS: Record<keyof Disclosures, string> = {
  priorAccident: 'Have you been involved in an aircraft accident or incident?',
  priorEnforcement: 'Have you ever had an FAA enforcement action against you?',
  priorInsuranceRefusal: 'Has an aviation insurer ever refused, cancelled, or declined to renew you?',
  dui: 'Have you had a DUI/DWI or any drug/alcohol-related driving conviction?',
  pendingActions: 'Are there any pending FAA actions or investigations involving you?',
};

const EMPTY_EC: EmergencyContact = { name: '', relationship: '', phone: '' };
const EMPTY_PERSONAL: PersonalInfo = {
  firstName: '',
  lastName: '',
  dateOfBirth: '',
  address: '',
  city: '',
  state: '',
  zip: '',
  phone: '',
  emergencyContact: EMPTY_EC,
};
const EMPTY_PILOT: PilotInfo = {
  certificateType: '',
  certificateNumber: '',
  certificateIssueDate: '',
  ratings: [],
  medicalClass: '',
  medicalIssueDate: '',
  medicalExpiration: '',
  medicalLimitations: '',
  lastBFR: '',
  bfrExpiration: '',
  lastIPC: '',
  ipcExpiration: '',
  totalHours: 0,
  hoursIn172: 0,
  hoursLast90: 0,
  hoursLast12Months: 0,
  intendedLeaseDuration: '',
  flyingGoal: '',
};
const EMPTY_INSURANCE: InsuranceInfo = {
  carrier: '',
  policyNumber: '',
  liabilityLimit: 0,
  expirationDate: '',
};
const EMPTY_DISC: Disclosures = {
  priorAccident: { answer: '', explanation: '' },
  priorEnforcement: { answer: '', explanation: '' },
  priorInsuranceRefusal: { answer: '', explanation: '' },
  dui: { answer: '', explanation: '' },
  pendingActions: { answer: '', explanation: '' },
};

// The documents the wizard captures, with the expiration date each one tracks
// (read from the matching profile field at submit time so the doc record and
// the profile field never disagree).
const STEP_KEYS = [
  'welcome',
  'personal',
  'certificate',
  'medical',
  'flightTime',
  'license',
  'badge',
  'disclosures',
  'payment',
  'insurance',
  'review',
] as const;
type StepKey = (typeof STEP_KEYS)[number];

const STEP_TITLES: Record<StepKey, string> = {
  welcome: 'Welcome aboard',
  personal: 'About you',
  certificate: 'Pilot certificate',
  medical: 'Medical certificate',
  flightTime: 'Flight time',
  license: 'Government ID',
  badge: 'KLGB airport badge',
  insurance: 'Renter’s insurance',
  disclosures: 'Background',
  payment: 'Payment method',
  review: 'Review & submit',
};

export function Onboarding() {
  const { fbUser, profile, demoMode, exitDemo, signOut } = useAuth();
  const navigate = useNavigate();

  async function leaveOnboarding() {
    if (demoMode) {
      exitDemo();
    } else {
      await signOut();
    }
    navigate('/login', { replace: true });
  }

  // Resume at the saved step if there's a draft.
  const [stepIdx, setStepIdx] = useState(() => {
    const saved = profile?.onboardingDraft?.step;
    const i = saved ? STEP_KEYS.indexOf(saved as StepKey) : -1;
    return i >= 0 ? i : 0;
  });
  const step = STEP_KEYS[stepIdx];

  const [personal, setPersonal] = useState<PersonalInfo>({
    ...EMPTY_PERSONAL,
    ...(profile?.personal as PersonalInfo),
    emergencyContact: { ...EMPTY_EC, ...(profile?.personal?.emergencyContact as EmergencyContact) },
  });
  const [pilot, setPilot] = useState<PilotInfo>({ ...EMPTY_PILOT, ...(profile?.pilot as PilotInfo) });
  const [disclosures, setDisclosures] = useState<Disclosures>({
    ...EMPTY_DISC,
    ...(profile?.disclosures as Disclosures),
  });
  const [insurance, setInsurance] = useState<InsuranceInfo>({
    ...EMPTY_INSURANCE,
    ...(profile?.insurance as InsuranceInfo),
  });

  // Local capture previews for this session, keyed by type.
  const [photos, setPhotos] = useState<Partial<Record<DocumentType, CapturedPhoto>>>({});
  // Documents already uploaded (persisted) — seeded from Firestore on mount and
  // updated as photos upload. This is what makes resume keep the photos.
  const [uploadedDocs, setUploadedDocs] = useState<
    Partial<Record<DocumentType, { documentId: string; url: string }>>
  >({});
  const [uploading, setUploading] = useState<Partial<Record<DocumentType, boolean>>>({});
  const [badgeExp, setBadgeExp] = useState(profile?.onboardingDraft?.badgeExp ?? '');
  const [licenseExp, setLicenseExp] = useState(profile?.onboardingDraft?.licenseExp ?? '');

  const [submitting, setSubmitting] = useState(false);
  const [submitErr, setSubmitErr] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  // Whether the lease has been signed (persisted, immutable). Locks the step.
  const [signedLease, setSignedLease] = useState(false);
  const [signingLease, setSigningLease] = useState(false);
  // Card-on-file (persisted server-side by the Stripe function). Resumes too.
  const [cardOnFile, setCardOnFile] = useState(Boolean(profile?.paymentMethodOnFile));
  // Finalize a card saved via Stripe's hosted page when it redirects back here.
  useCardReturn(() => setCardOnFile(true));
  // (The old $500 insurance-contribution choice was removed 2026-07-17: lessees now
  // carry their own renter's insurance instead — required at the insurance step.)
  // Pricing plan. Everyone is a lessee on the 'standard' plan unless they apply a
  // valid promo code → 'promo' ($75/hr, no minimum, month-to-month). The owner
  // confirms the plan at activation.
  const [plan, setPlan] = useState<'standard' | 'promo'>(profile?.plan === 'promo' ? 'promo' : 'standard');
  const [promoCode, setPromoCode] = useState(profile?.promoCode ?? '');
  const [promoBusy, setPromoBusy] = useState(false);
  const [promoStatus, setPromoStatus] = useState<{ ok: boolean; msg: string } | null>(null);

  // Validate the promo code server-side (the codes live in the /api/validate-promo
  // function, never in the bundle). Demo mode checks locally. Owner confirms the
  // resulting plan at activation, so a guessed code can't grant the discount alone.
  async function applyPromo() {
    const code = promoCode.trim();
    if (!code) return;
    setPromoBusy(true);
    setPromoStatus(null);
    try {
      let valid = false;
      if (demoMode) {
        valid = code.toLowerCase() === 'rightrudder';
      } else {
        const r = await fetch(`${import.meta.env.BASE_URL}api/validate-promo`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code }),
        });
        valid = (await r.json())?.valid === true;
      }
      if (valid) {
        setPlan('promo');
        setPromoStatus({ ok: true, msg: 'Promo applied — $75/Hobbs-hr, no monthly minimum, month-to-month.' });
      } else {
        setPlan('standard');
        setPromoStatus({ ok: false, msg: 'That code isn’t valid.' });
      }
    } catch {
      setPromoStatus({ ok: false, msg: 'Couldn’t check the code right now. Try again.' });
    } finally {
      setPromoBusy(false);
    }
  }

  // On resume, load any documents already uploaded so those steps show as done.
  useEffect(() => {
    if (demoMode || !fbUser || !db) return;
    let active = true;
    (async () => {
      const snap = await getDocs(
        query(collection(db, 'documents'), where('ownerUid', '==', fbUser.uid))
      );
      if (!active) return;
      const map: Partial<Record<DocumentType, { documentId: string; url: string }>> = {};
      snap.forEach((d) => {
        const data = d.data() as { type: DocumentType; fileRef: string };
        map[data.type] = { documentId: d.id, url: data.fileRef };
      });
      // This-session uploads (prev) take precedence over what we just loaded.
      setUploadedDocs((prev) => ({ ...map, ...prev }));
    })();
    return () => {
      active = false;
    };
  }, [demoMode, fbUser]);

  // On resume, check whether the lease was already signed (write-once record).
  useEffect(() => {
    if (demoMode || !fbUser || !db) return;
    let active = true;
    (async () => {
      const snap = await getDocs(
        query(collection(db, 'leaseSignatures'), where('signerUid', '==', fbUser.uid))
      );
      if (active && !snap.empty) setSignedLease(true);
    })();
    return () => {
      active = false;
    };
  }, [demoMode, fbUser]);

  // Sign the lease: write the immutable, legally-binding record. Once it exists
  // the step is locked — a signed lease can't be re-signed by re-landing on it.
  async function signLease(sig: SignatureResult, consent: boolean) {
    if (signedLease || signingLease) return;
    setSubmitErr(null);
    setSigningLease(true);
    try {
      if (demoMode) {
        await new Promise((r) => setTimeout(r, 600));
        setSignedLease(true);
        return;
      }
      if (!fbUser || !db || !storage) throw new Error('Not signed in.');

      const signerName = `${personal.firstName} ${personal.lastName}`.trim();
      const signDate = localToday();
      // Everyone signs the dry lease; promo-plan lessees also sign the Promo Addendum.
      const mergeData = { pilotName: signerName, pilotAddress: formatAddress(personal), signDate };
      const agreementText = renderAgreementForPlan(mergeData, plan);
      const agreementHash = await sha256(agreementText);

      // Upload the signature image (reuses the documents/{uid} storage path).
      const blob = await (await fetch(sig.dataUrl)).blob();
      const sref = storageRef(storage, `documents/${fbUser.uid}/lease-signature-${Date.now()}.png`);
      await uploadBytes(sref, blob);
      const signatureImageRef = await getDownloadURL(sref);

      // Best-effort IP for the binding record.
      let ipAddress = '';
      try {
        const meta = await fetch(`${import.meta.env.BASE_URL}api/client-meta`).then((r) => r.json());
        ipAddress = meta?.ip ?? '';
      } catch {
        /* non-fatal */
      }

      const created = await addDoc(collection(db, 'leaseSignatures'), {
        signerUid: fbUser.uid,
        signerName,
        signerEmail: fbUser.email ?? '',
        aircraftId: TRACKED_REG,
        agreementType: 'lease',
        agreementVersion: LEASE_VERSION,
        agreementText,
        agreementHash,
        signatureImageRef,
        signatureMethod: sig.method,
        consentAcknowledged: consent,
        ipAddress,
        userAgent: navigator.userAgent,
        signedAt: serverTimestamp(),
      });
      await updateDoc(doc(db, 'leaseSignatures', created.id), { signatureId: created.id });
      setSignedLease(true);
    } catch (e) {
      setSubmitErr(e instanceof Error ? e.message : 'Could not record your signature. Try again.');
    } finally {
      setSigningLease(false);
    }
  }

  // Whether a step's document is satisfied (uploaded for real, or captured in demo).
  const hasDoc = (t: DocumentType) => (demoMode ? Boolean(photos[t]) : Boolean(uploadedDocs[t]));

  // Capture handler: show a local preview, then upload immediately so the photo
  // survives a sign-out / page reload. Replaces the existing doc on retake.
  async function handlePhoto(type: DocumentType, photo: CapturedPhoto | null) {
    setPhotos((prev) => {
      const next = { ...prev };
      if (photo) next[type] = photo;
      else delete next[type];
      return next;
    });
    if (!photo || demoMode || !fbUser || !db || !storage) return;
    setUploading((u) => ({ ...u, [type]: true }));
    try {
      const path = `documents/${fbUser.uid}/${Date.now()}-${type}-${photo.file.name}`;
      const sref = storageRef(storage, path);
      await uploadBytes(sref, photo.file);
      const url = await getDownloadURL(sref);
      const existingId = uploadedDocs[type]?.documentId;
      if (existingId) {
        await updateDoc(doc(db, 'documents', existingId), {
          fileRef: url,
          fileName: photo.file.name,
          verificationStatus: 'pending',
          uploadedAt: serverTimestamp(),
        });
        setUploadedDocs((d) => ({ ...d, [type]: { documentId: existingId, url } }));
      } else {
        const created = await addDoc(collection(db, 'documents'), {
          ownerUid: fbUser.uid,
          type,
          fileRef: url,
          fileName: photo.file.name,
          expirationDate: null,
          verificationStatus: 'pending',
          uploadedAt: serverTimestamp(),
        });
        await updateDoc(doc(db, 'documents', created.id), { documentId: created.id });
        setUploadedDocs((d) => ({ ...d, [type]: { documentId: created.id, url } }));
      }
    } catch {
      setSubmitErr('Could not save that photo — please try again.');
    } finally {
      setUploading((u) => ({ ...u, [type]: false }));
    }
  }

  // Persist the draft (text fields + position) so onboarding resumes mid-flow.
  function saveProgress(stepKey: StepKey) {
    if (demoMode || !fbUser || !db) return;
    updateDoc(doc(db, 'users', fbUser.uid), {
      personal,
      pilot,
      disclosures,
      insurance,
      billingProfile: 'lessee',
      plan,
      promoCode: promoCode || null,
      onboardingDraft: { step: stepKey, licenseExp, badgeExp },
    }).catch(() => {});
  }

  const canAdvance = useMemo(() => {
    // Preview mode: every step is pre-filled, so let the owner click straight
    // through the wizard without uploading docs or signing.
    if (demoMode) return true;
    switch (step) {
      case 'personal':
        return Boolean(
          personal.firstName &&
            personal.lastName &&
            personal.dateOfBirth &&
            personal.phone &&
            personal.address &&
            personal.city &&
            personal.state &&
            personal.zip &&
            personal.emergencyContact.name &&
            personal.emergencyContact.phone
        );
      case 'certificate':
        return Boolean(
          hasDoc('pilot_certificate') &&
            hasDoc('pilot_certificate_back') &&
            pilot.certificateType &&
            pilot.certificateNumber
        );
      case 'flightTime':
        return Boolean(pilot.intendedLeaseDuration);
      case 'medical':
        // Student with no medical yet: dual is fine, solo needs one (lease §4.3(b)).
        if (isStudentPilot(pilot) && !hasDoc('medical') && !pilot.medicalExpiration) return true;
        return Boolean(hasDoc('medical') && pilot.medicalClass && pilot.medicalExpiration);
      case 'license':
        return Boolean(hasDoc('drivers_license') && licenseExp);
      case 'badge':
        return true; // Not a blocker at onboarding — §9.3 allows escorted ramp access until the badge is issued
      case 'insurance':
        // Renter's insurance is required by the lease (§11.2) BEFORE FLYING, but it
        // doesn't block finishing onboarding — missing/expired coverage is flagged
        // in the owner's review email instead (see submit()).
        return true;
      case 'disclosures':
        return (Object.keys(disclosures) as Array<keyof Disclosures>).every(
          (k) => disclosures[k].answer !== ''
        );
      case 'payment':
        // A card on file is required before the first flight, but it does NOT block
        // onboarding — a pilot can finish now and add/fix the card later from Billing
        // (avoids a hard wall if the in-browser card form stalls). Missing card is
        // flagged in the owner's review email (see submit()).
        return true;
      default:
        return true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, personal, pilot, disclosures, insurance, photos, uploadedDocs, demoMode, licenseExp, signedLease, cardOnFile]);

  // When the pilot presses Next before the step is complete, list exactly what's
  // still needed instead of leaving them at a dead button with no explanation.
  const [nudge, setNudge] = useState<string[] | null>(null);
  useEffect(() => {
    if (canAdvance) setNudge(null);
  }, [canAdvance]);

  function missingForStep(): string[] {
    const out: string[] = [];
    switch (step) {
      case 'personal':
        if (!personal.firstName) out.push('First name');
        if (!personal.lastName) out.push('Last name');
        if (!personal.dateOfBirth) out.push('Date of birth');
        if (!personal.phone) out.push('Phone number');
        if (!personal.address) out.push('Street address');
        if (!personal.city) out.push('City');
        if (!personal.state) out.push('State');
        if (!personal.zip) out.push('ZIP code');
        if (!personal.emergencyContact.name) out.push('Emergency contact name');
        if (!personal.emergencyContact.phone) out.push('Emergency contact phone');
        break;
      case 'certificate':
        if (!hasDoc('pilot_certificate')) out.push('A photo of the FRONT of your pilot certificate');
        if (!hasDoc('pilot_certificate_back'))
          out.push('A photo of the BACK of your pilot certificate (it shows your ratings)');
        if (!pilot.certificateType) out.push('Certificate type');
        if (!pilot.certificateNumber) out.push('Certificate number');
        break;
      case 'flightTime':
        if (!pilot.intendedLeaseDuration) out.push(`How long you plan to lease ${TRACKED_REG}`);
        break;
      case 'medical':
        // A student may fly dual before holding a medical (lease §4.3(b)); it's needed before solo.
        if (isStudentPilot(pilot) && !hasDoc('medical') && !pilot.medicalExpiration) break;
        if (!hasDoc('medical')) out.push('A photo of your medical certificate');
        if (!pilot.medicalClass) out.push('Medical class');
        if (!pilot.medicalExpiration) out.push('Medical expiration date');
        break;
      case 'license':
        if (!hasDoc('drivers_license')) out.push('A photo of your government ID');
        if (!licenseExp) out.push('ID expiration date');
        break;
      case 'disclosures':
        if (
          (Object.keys(disclosures) as Array<keyof Disclosures>).some(
            (k) => disclosures[k].answer === ''
          )
        )
          out.push('A Yes or No answer to every question');
        break;
      default:
        break;
    }
    return out;
  }

  function next() {
    if (!canAdvance) {
      setNudge(missingForStep());
      return;
    }
    setNudge(null);
    const ni = Math.min(stepIdx + 1, STEP_KEYS.length - 1);
    setStepIdx(ni);
    saveProgress(STEP_KEYS[ni]);
    if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
  }
  function back() {
    setNudge(null);
    setStepIdx((i) => Math.max(i - 1, 0));
    if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  async function submit() {
    setSubmitErr(null);
    setSubmitting(true);
    // A pilot auto-activates only if BOTH: (1) a clean background questionnaire
    // and (2) they meet the insurance Open Pilot Clause. Otherwise → owner review.
    const disclosureItems = (Object.keys(disclosures) as Array<keyof Disclosures>)
      .filter((k) => disclosures[k].answer === 'yes')
      .map((k) => DISCLOSURE_LABELS[k]);
    const elig = checkEligibility(pilot);
    // Renter's insurance (lease §11.2) is required before flying but doesn't block
    // onboarding — surface missing/expired coverage in the owner's review email.
    const insReasons: string[] = [];
    const hasInsDoc = Boolean(uploadedDocs.renters_insurance);
    const insCurrent =
      insurance.expirationDate && new Date(insurance.expirationDate).getTime() > Date.now();
    if (!hasInsDoc || !insurance.carrier || !insurance.policyNumber)
      insReasons.push("Renter's insurance not on file yet — required before solo flight (14-day window from activation).");
    else if (!insCurrent) insReasons.push("Renter's insurance appears expired — verify before solo flight.");
    // Card on file is required before the first flight but doesn't block onboarding —
    // flag it for the owner if they finished without saving one.
    const cardReasons = cardOnFile ? [] : ['No payment card on file yet — required before first flight.'];
    const flagReasons = [...disclosureItems, ...elig.reasons, ...insReasons, ...cardReasons];
    const flagged = flagReasons.length > 0;
    try {
      if (demoMode) {
        await new Promise((r) => setTimeout(r, 900));
        setDone(true);
        return;
      }
      if (!fbUser || !db) throw new Error('Not signed in.');

      // Photos already uploaded as they were captured (handlePhoto). Now stamp
      // each document with the expiration confirmed in its step.
      const docExpirations: Partial<Record<DocumentType, string>> = {
        medical: pilot.medicalExpiration,
        drivers_license: licenseExp,
        renters_insurance: insurance.expirationDate,
        lgb_badge: badgeExp,
      };
      for (const [type, info] of Object.entries(uploadedDocs) as Array<
        [DocumentType, { documentId: string; url: string }]
      >) {
        const exp = docExpirations[type];
        // Best-effort: stamping an expiration must never block submission. The
        // owner can set/confirm expirations at review, and the profile fields
        // already carry them.
        if (exp) {
          try {
            await updateDoc(doc(db, 'documents', info.documentId), { expirationDate: exp });
          } catch (err) {
            console.warn('Could not stamp expiration on document', type, err);
          }
        }
      }

      // EVERY pilot goes into owner review after onboarding — nobody auto-activates.
      // The owner reviews their documents + questionnaire, then approves, which flips
      // them to 'active' and emails them a congratulations. (flagReasons still ride
      // along in the owner email so disclosures get extra scrutiny.)
      await updateDoc(doc(db, 'users', fbUser.uid), {
        personal,
        pilot,
        disclosures,
        insurance,
        billingProfile: 'lessee',
        plan, // 'standard' or 'promo' (if a valid promo code was applied)
        promoCode: promoCode || null,
        status: 'verification_pending',
        submittedForVerificationAt: serverTimestamp(),
        onboardingCompletedAt: serverTimestamp(),
      });

      // Email the owner that a pilot is ready to review.
      const pilotName = `${personal.firstName} ${personal.lastName}`.trim();
      sendAlert('pilot_pending_review', {
        name: pilotName,
        email: fbUser.email,
        certificate: `${pilot.certificateType} ${pilot.certificateNumber}`.trim(),
        leaseLength: LEASE_DURATION_LABEL[pilot.intendedLeaseDuration] ?? '—',
        goal: pilot.flyingGoal || '—',
        flagged: flagged ? flagReasons.join('; ') : 'None — clean questionnaire',
      });

      setDone(true);
    } catch (e) {
      setSubmitErr(e instanceof Error ? e.message : 'Could not submit. Try again.');
    } finally {
      setSubmitting(false);
    }
  }

  if (done) {
    return (
      <div className="mx-auto max-w-lg px-4 py-16 text-center">
        <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100">
          <ShieldCheck className="text-emerald-700" size={32} />
        </div>
        <h1 className="font-display text-2xl font-semibold text-navy-900">
          Thanks — we’ll be in touch
        </h1>
        <p className="mt-3 text-ink-muted">
          Your profile, documents, questionnaire, and signed lease are all in. The owner will review
          everything and approve your account — you’ll get an email the moment you’re cleared to book
          {TRACKED_REG}. You can sign in anytime to check your status.
        </p>
        {demoMode ? (
          <>
            <p className="mt-4 text-xs text-amber-700">
              Demo: nothing was saved. Exit demo to return to the role picker.
            </p>
            <button className="btn-primary mt-4" onClick={exitDemo}>
              Exit demo
            </button>
          </>
        ) : (
          <button className="btn-primary mt-6" onClick={() => navigate('/')}>
            Go to dashboard
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sky-50/40">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-2xl items-center justify-between gap-3 px-4 py-3">
          <Logo className="text-navy-900 text-[1.7rem]" />
          <div className="flex items-center gap-4">
            <span className="text-xs font-medium text-ink-muted">
              Step {stepIdx + 1} of {STEP_KEYS.length}
            </span>
            <button
              type="button"
              onClick={leaveOnboarding}
              className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-xs font-medium text-ink-muted hover:bg-slate-100 hover:text-navy-900"
            >
              <LogOut size={14} /> {demoMode ? 'Exit preview' : 'Save & sign out'}
            </button>
          </div>
        </div>
        <div className="h-1 w-full bg-slate-100">
          <div
            className="h-full bg-brand transition-all"
            style={{ width: `${((stepIdx + 1) / STEP_KEYS.length) * 100}%` }}
          />
        </div>
      </header>

      <main className="mx-auto max-w-2xl px-4 py-8">
        <h1 className="font-display text-2xl font-semibold text-navy-900">
          {STEP_TITLES[step]}
        </h1>

        <div className="mt-6">
          {step === 'welcome' && <WelcomeStep firstName={personal.firstName} demoMode={demoMode} />}
          {step === 'personal' && (
            <PersonalStep
              personal={personal}
              setPersonal={setPersonal}
              plan={plan}
              promoCode={promoCode}
              setPromoCode={setPromoCode}
              applyPromo={applyPromo}
              promoBusy={promoBusy}
              promoStatus={promoStatus}
            />
          )}

          {step === 'certificate' && (
            <CertificateStep
              pilot={pilot}
              setPilot={setPilot}
              photo={photos.pilot_certificate ?? null}
              setPhoto={(p) => handlePhoto('pilot_certificate', p)}
              setPhotoBack={(p) => handlePhoto('pilot_certificate_back', p)}
              existingUrl={uploadedDocs.pilot_certificate?.url}
              existingUrlBack={uploadedDocs.pilot_certificate_back?.url}
              demoMode={demoMode}
            />
          )}

          {step === 'medical' && (
            <MedicalStep
              pilot={pilot}
              setPilot={setPilot}
              photo={photos.medical ?? null}
              setPhoto={(p) => handlePhoto('medical', p)}
              existingUrl={uploadedDocs.medical?.url}
              demoMode={demoMode}
            />
          )}


          {step === 'flightTime' && <FlightTimeStep pilot={pilot} setPilot={setPilot} />}

          {step === 'license' && (
            <LicenseStep
              personal={personal}
              setPersonal={setPersonal}
              licenseExp={licenseExp}
              setLicenseExp={setLicenseExp}
              photo={photos.drivers_license ?? null}
              setPhoto={(p) => handlePhoto('drivers_license', p)}
              existingUrl={uploadedDocs.drivers_license?.url}
              demoMode={demoMode}
            />
          )}

          {step === 'badge' && (
            <BadgeStep
              badgeExp={badgeExp}
              setBadgeExp={setBadgeExp}
              photo={photos.lgb_badge ?? null}
              setPhoto={(p) => handlePhoto('lgb_badge', p)}
              existingUrl={uploadedDocs.lgb_badge?.url}
            />
          )}

          {step === 'insurance' && (
            <InsuranceStep
              insurance={insurance}
              setInsurance={setInsurance}
              setPhoto={(p) => handlePhoto('renters_insurance', p)}
              existingUrl={uploadedDocs.renters_insurance?.url}
              demoMode={demoMode}
            />
          )}

          {step === 'disclosures' && (
            <DisclosuresStep disclosures={disclosures} setDisclosures={setDisclosures} />
          )}

          {step === 'payment' && (
            <div className="space-y-4">
              <PaymentCardStep
                demoMode={demoMode}
                savedCard={
                  cardOnFile
                    ? { brand: profile?.cardBrand, last4: profile?.cardLast4 ?? '••••' }
                    : null
                }
                onSaved={() => setCardOnFile(true)}
              />
            </div>
          )}

          {step === 'review' && (
            <ReviewStep
              personal={personal}
              pilot={pilot}
              photoCount={new Set([...Object.keys(uploadedDocs), ...Object.keys(photos)]).size}
            />
          )}
        </div>

        {submitErr && (
          <div className="mt-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {submitErr}
          </div>
        )}

        {nudge && nudge.length > 0 && (
          <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-900">
            <div className="font-semibold">Almost — this step still needs:</div>
            <ul className="mt-1 list-disc space-y-0.5 pl-5">
              {nudge.map((m) => (
                <li key={m}>{m}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="mt-8 flex items-center justify-between">
          <button
            type="button"
            onClick={back}
            disabled={stepIdx === 0 || submitting}
            className="btn-secondary inline-flex items-center gap-1 disabled:opacity-40"
          >
            <ChevronLeft size={16} /> Back
          </button>

          {step === 'review' ? (
            <button
              type="button"
              onClick={submit}
              disabled={submitting}
              className="btn-primary inline-flex items-center gap-2"
            >
              {submitting ? 'Submitting…' : 'Submit for review'}
              {!submitting && <Check size={16} />}
            </button>
          ) : (
            <button
              type="button"
              onClick={next}
              className={`btn-primary inline-flex items-center gap-1 ${canAdvance ? '' : 'opacity-70'}`}
            >
              Next <ChevronRight size={16} />
            </button>
          )}
        </div>
      </main>
    </div>
  );
}

/* ----------------------------- shared bits ----------------------------- */

function Field({
  label,
  value,
  onChange,
  type = 'text',
  placeholder,
  className,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
  className?: string;
}) {
  return (
    <div className={className}>
      <label className="label">{label}</label>
      {type === 'date' || type === 'datetime-local' ? (
        <DateField
          type={type}
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
        />
      ) : (
        <input
          className="input"
          type={type}
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
    </div>
  );
}

// Renders capture + (after a photo) the AI-read banner. Calls onExtract with the
// fields once the photo is read, so each step decides where the values land.
function CaptureBlock({
  subject,
  docType,
  setPhoto,
  existingUrl,
  demoMode,
  onExtract,
}: {
  subject: string;
  docType: ExtractableDocType;
  setPhoto: (p: CapturedPhoto | null) => void;
  existingUrl?: string;
  demoMode: boolean;
  onExtract: (r: ExtractResult) => void;
}) {
  const [reading, setReading] = useState(false);
  const [result, setResult] = useState<ExtractResult | null>(null);

  async function handleChange(p: CapturedPhoto | null) {
    setPhoto(p);
    setResult(null);
    if (!p) return;
    setReading(true);
    const r = await extractDocument(docType, p.dataUrl, demoMode);
    setResult(r);
    onExtract(r);
    setReading(false);
  }

  return (
    <div>
      <PhotoCapture
        subject={subject}
        onChange={handleChange}
        busy={reading}
        initialPreview={existingUrl}
      />
      {reading && (
        <div className="mt-3 inline-flex items-center gap-2 text-sm text-navy-700">
          <Sparkles size={16} className="animate-pulse" /> Reading your {subject}…
        </div>
      )}
      {!reading && result && result.source !== 'manual' && (
        <div className="mt-3 flex items-start gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
          <Sparkles size={16} className="mt-0.5 shrink-0" />
          <span>
            We read this from your photo{result.source === 'demo' ? ' (sample data)' : ''} — please
            confirm the details below are correct.
          </span>
        </div>
      )}
      {!reading && result?.source === 'manual' && result.message && (
        <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          {result.message}
        </div>
      )}
    </div>
  );
}

/* ------------------------------- steps -------------------------------- */

function WelcomeStep({ firstName, demoMode }: { firstName: string; demoMode: boolean }) {
  return (
    <div className="card p-6">
      <p className="text-ink-muted">
        Welcome{firstName ? `, ${firstName}` : ''}. Let’s get you set up to fly {TRACKED_REG}. This takes
        about 5 minutes — have these handy and we’ll read most of the details straight from the
        photos:
      </p>
      <ul className="mt-4 space-y-2 text-sm text-navy-900">
        {[
          'Your pilot certificate',
          'Your FAA medical certificate',
          'Your driver’s license or government ID',
          'Your Long Beach (KLGB) airport badge — needed to reach the airplane without an escort',
          'Proof of renter’s / non-owned aircraft insurance (required before you fly solo — you have 14 days)',
          'A credit card (kept on file — you’re billed per flight, no monthly minimum)',
        ].map((t) => (
          <li key={t} className="flex items-center gap-2">
            <Check size={16} className="text-emerald-600" /> {t}
          </li>
        ))}
      </ul>
      {demoMode && (
        <p className="mt-4 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800">
          Demo mode: photos won’t be uploaded and document reading returns sample data.
        </p>
      )}
    </div>
  );
}

function PersonalStep({
  personal,
  setPersonal,
  plan,
  promoCode,
  setPromoCode,
  applyPromo,
  promoBusy,
  promoStatus,
}: {
  personal: PersonalInfo;
  setPersonal: (p: PersonalInfo) => void;
  plan: 'standard' | 'promo';
  promoCode: string;
  setPromoCode: (c: string) => void;
  applyPromo: () => void;
  promoBusy: boolean;
  promoStatus: { ok: boolean; msg: string } | null;
}) {
  const ec = personal.emergencyContact;
  return (
    <div className="space-y-6">
      <div className="card p-6">
        <h2 className="font-display text-lg font-semibold text-navy-900 mb-1">Your plan</h2>
        <p className="mb-4 text-sm text-ink-muted">
          Have a promo code? Enter it to apply a special plan. The owner confirms your plan before
          you’re activated.
        </p>
        <div className="rounded-xl border-2 border-slate-200 p-4">
          <div className="font-semibold text-navy-900">
            {plan === 'promo' ? 'Promo plan applied' : 'Standard dry lease'}
          </div>
          <div className="mt-0.5 text-sm text-ink-muted">
            {plan === 'promo'
              ? '$75 / Hobbs-hour · no monthly minimum · month-to-month.'
              : '$100 / Hobbs-hour · no monthly minimum · month-to-month.'}
          </div>
          <div className="mt-3 flex gap-2">
            <input
              className="input flex-1"
              placeholder="Promo code (optional)"
              value={promoCode}
              onChange={(e) => setPromoCode(e.target.value)}
            />
            <button
              type="button"
              onClick={applyPromo}
              disabled={promoBusy || !promoCode.trim()}
              className="btn-secondary whitespace-nowrap disabled:opacity-40"
            >
              {promoBusy ? 'Checking…' : 'Apply'}
            </button>
          </div>
          {promoStatus && (
            <p className={`mt-2 text-xs ${promoStatus.ok ? 'text-emerald-700' : 'text-red-600'}`}>
              {promoStatus.msg}
            </p>
          )}
        </div>
      </div>

      <div className="card p-6">
        <h2 className="font-display text-lg font-semibold text-navy-900 mb-4">Your details</h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="First name" value={personal.firstName} onChange={(v) => setPersonal({ ...personal, firstName: v })} />
          <Field label="Last name" value={personal.lastName} onChange={(v) => setPersonal({ ...personal, lastName: v })} />
          <Field label="Date of birth" type="date" value={personal.dateOfBirth} onChange={(v) => setPersonal({ ...personal, dateOfBirth: v })} />
          <Field label="Phone" type="tel" placeholder={PHONE_MASK} value={personal.phone} onChange={(v) => setPersonal({ ...personal, phone: formatPhone(v) })} />
          <Field className="sm:col-span-2" label="Street address" placeholder="1369 Adams Ave, Apt 3414" value={personal.address} onChange={(v) => setPersonal({ ...personal, address: v })} />
          <Field label="City" value={personal.city ?? ''} onChange={(v) => setPersonal({ ...personal, city: v })} />
          <div className="grid grid-cols-2 gap-4">
            <Field label="State" placeholder="CA" value={personal.state ?? ''} onChange={(v) => setPersonal({ ...personal, state: v.toUpperCase().slice(0, 2) })} />
            <Field label="ZIP" placeholder="92626" value={personal.zip ?? ''} onChange={(v) => setPersonal({ ...personal, zip: v.replace(/[^0-9-]/g, '').slice(0, 10) })} />
          </div>
        </div>
      </div>
      <div className="card p-6">
        <h2 className="font-display text-lg font-semibold text-navy-900 mb-4">Emergency contact</h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Field label="Name" value={ec.name} onChange={(v) => setPersonal({ ...personal, emergencyContact: { ...ec, name: v } })} />
          <Field label="Relationship" value={ec.relationship} onChange={(v) => setPersonal({ ...personal, emergencyContact: { ...ec, relationship: v } })} />
          <Field label="Phone" type="tel" placeholder={PHONE_MASK} value={ec.phone} onChange={(v) => setPersonal({ ...personal, emergencyContact: { ...ec, phone: formatPhone(v) } })} />
        </div>
      </div>
    </div>
  );
}

function CertificateStep({
  pilot,
  setPilot,
  setPhoto,
  setPhotoBack,
  existingUrl,
  existingUrlBack,
  demoMode,
}: {
  pilot: PilotInfo;
  setPilot: (p: PilotInfo) => void;
  photo: CapturedPhoto | null;
  setPhoto: (p: CapturedPhoto | null) => void;
  setPhotoBack: (p: CapturedPhoto | null) => void;
  existingUrl?: string;
  existingUrlBack?: string;
  demoMode: boolean;
}) {
  return (
    <div className="card p-6 space-y-5">
      <CaptureBlock
        subject="pilot certificate — FRONT"
        docType="pilot_certificate"
        setPhoto={setPhoto}
        existingUrl={existingUrl}
        demoMode={demoMode}
        onExtract={(r) =>
          setPilot({
            ...pilot,
            certificateType: r.fields.certificateType || pilot.certificateType,
            certificateNumber: r.fields.certificateNumber || pilot.certificateNumber,
            certificateIssueDate: r.fields.certificateIssueDate || pilot.certificateIssueDate,
            ratings: r.fields.ratings?.length ? r.fields.ratings : pilot.ratings,
          })
        }
      />
      {/* The front carries the name and certificate number; the RATINGS are printed
          on the back. Without it there is no way to confirm airplane single-engine
          land, which lease §4.3(a) requires by name and PB214 conditions coverage on. */}
      <CaptureBlock
        subject="pilot certificate — BACK (ratings)"
        docType="pilot_certificate_back"
        setPhoto={setPhotoBack}
        existingUrl={existingUrlBack}
        demoMode={demoMode}
        onExtract={(r) =>
          setPilot({
            ...pilot,
            ratings: r.fields.ratings?.length ? r.fields.ratings : pilot.ratings,
          })
        }
      />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label className="label">Certificate type</label>
          <select
            className="input"
            value={pilot.certificateType}
            onChange={(e) => setPilot({ ...pilot, certificateType: e.target.value })}
          >
            <option value="">Select…</option>
            <option>Student Pilot</option>
            <option>Sport Pilot</option>
            <option>Recreational Pilot</option>
            <option>Private Pilot</option>
            <option>Commercial Pilot</option>
            <option>Airline Transport Pilot (ATP)</option>
            <option>Flight Instructor (CFI)</option>
            <option>Flight Instructor – Instrument (CFII)</option>
            <option>Multi-Engine Instructor (MEI)</option>
          </select>
        </div>
        <Field label="Certificate number" value={pilot.certificateNumber} onChange={(v) => setPilot({ ...pilot, certificateNumber: v })} />
        <Field label="Issue date" type="date" value={pilot.certificateIssueDate} onChange={(v) => setPilot({ ...pilot, certificateIssueDate: v })} />
      </div>
      <div>
        <label className="label">Ratings &amp; endorsements</label>
        <div className="mt-1 flex flex-wrap gap-2">
          {RATING_OPTIONS.map((r) => {
            const on = pilot.ratings.includes(r);
            return (
              <button
                key={r}
                type="button"
                onClick={() =>
                  setPilot({
                    ...pilot,
                    ratings: on ? pilot.ratings.filter((x) => x !== r) : [...pilot.ratings, r],
                  })
                }
                className={`rounded-full border px-3 py-1.5 text-sm ${
                  on ? 'border-brand bg-brand text-white' : 'border-slate-300 text-ink-muted'
                }`}
              >
                {r}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function MedicalStep({
  pilot,
  setPilot,
  setPhoto,
  existingUrl,
  demoMode,
}: {
  pilot: PilotInfo;
  setPilot: (p: PilotInfo) => void;
  photo: CapturedPhoto | null;
  setPhoto: (p: CapturedPhoto | null) => void;
  existingUrl?: string;
  demoMode: boolean;
}) {
  return (
    <div className="card p-6 space-y-5">
      {isStudentPilot(pilot) && (
        <p className="rounded-lg bg-sky-50 px-4 py-3 text-sm text-ink-muted">
          Student pilot? If you don&rsquo;t have a medical yet you can skip this step and fly dual with
          your instructor. You&rsquo;ll need one on file before your first solo.
        </p>
      )}
      <CaptureBlock
        subject="medical certificate"
        docType="medical"
        setPhoto={setPhoto}
        existingUrl={existingUrl}
        demoMode={demoMode}
        onExtract={(r) =>
          setPilot({
            ...pilot,
            medicalClass: (r.fields.medicalClass as PilotInfo['medicalClass']) || pilot.medicalClass,
            medicalIssueDate: r.fields.medicalIssueDate || pilot.medicalIssueDate,
            medicalExpiration: r.fields.medicalExpiration || pilot.medicalExpiration,
            medicalLimitations: r.fields.medicalLimitations || pilot.medicalLimitations,
          })
        }
      />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label className="label">Class</label>
          <select
            className="input"
            value={pilot.medicalClass}
            onChange={(e) => setPilot({ ...pilot, medicalClass: e.target.value as PilotInfo['medicalClass'] })}
          >
            <option value="">Select…</option>
            <option value="1st">1st Class</option>
            <option value="2nd">2nd Class</option>
            <option value="3rd">3rd Class</option>
            <option value="basicmed">BasicMed</option>
          </select>
        </div>
        <Field label="Issue date" type="date" value={pilot.medicalIssueDate} onChange={(v) => setPilot({ ...pilot, medicalIssueDate: v })} />
        <Field label="Expiration" type="date" value={pilot.medicalExpiration} onChange={(v) => setPilot({ ...pilot, medicalExpiration: v })} />
        <Field label="Limitations" placeholder="e.g. corrective lenses" value={pilot.medicalLimitations} onChange={(v) => setPilot({ ...pilot, medicalLimitations: v })} />
      </div>
    </div>
  );
}

function FlightTimeStep({
  pilot,
  setPilot,
}: {
  pilot: PilotInfo;
  setPilot: (p: PilotInfo) => void;
}) {
  const num = (v: string) => Number(v) || 0;
  return (
    <div className="card p-6">
      <p className="mb-4 text-sm text-ink-muted">
        Your logged hours. There’s no insurance hour minimum to fly {TRACKED_REG} — these just give AJM a
        picture of your experience for approval and your checkout.
      </p>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Total hours" type="number" value={String(pilot.totalHours || '')} onChange={(v) => setPilot({ ...pilot, totalHours: num(v) })} />
        <Field label="Hours in Cessna 172" type="number" value={String(pilot.hoursIn172 || '')} onChange={(v) => setPilot({ ...pilot, hoursIn172: num(v) })} />
        <Field label="Hours last 90 days" type="number" value={String(pilot.hoursLast90 || '')} onChange={(v) => setPilot({ ...pilot, hoursLast90: num(v) })} />
        <Field label="Hours last 12 months" type="number" value={String(pilot.hoursLast12Months || '')} onChange={(v) => setPilot({ ...pilot, hoursLast12Months: num(v) })} />
      </div>
      <div className="mt-6 border-t border-slate-100 pt-5">
        <p className="mb-4 text-sm text-ink-muted">
          One more thing — how long do you see yourself flying {TRACKED_REG}? There’s no term or minimum
          either way; it just helps AJM plan.
        </p>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="label">How long do you plan to lease?</label>
            <select
              className="input"
              value={pilot.intendedLeaseDuration}
              onChange={(e) =>
                setPilot({ ...pilot, intendedLeaseDuration: e.target.value as PilotInfo['intendedLeaseDuration'] })
              }
            >
              <option value="">Select…</option>
              <option value="1-3mo">1–3 months</option>
              <option value="3-6mo">3–6 months</option>
              <option value="6-12mo">6–12 months</option>
              <option value="12mo+">A year or more / ongoing</option>
            </select>
          </div>
          <Field
            label="What are you working toward? (optional)"
            value={pilot.flyingGoal}
            onChange={(v) => setPilot({ ...pilot, flyingGoal: v })}
            placeholder="e.g. commercial cross-countries, IFR currency, just flying"
          />
        </div>
      </div>
    </div>
  );
}

function LicenseStep({
  personal,
  setPersonal,
  licenseExp,
  setLicenseExp,
  setPhoto,
  existingUrl,
  demoMode,
}: {
  personal: PersonalInfo;
  setPersonal: (p: PersonalInfo) => void;
  licenseExp: string;
  setLicenseExp: (v: string) => void;
  photo: CapturedPhoto | null;
  setPhoto: (p: CapturedPhoto | null) => void;
  existingUrl?: string;
  demoMode: boolean;
}) {
  return (
    <div className="card p-6 space-y-5">
      <CaptureBlock
        subject="driver’s license or ID"
        docType="drivers_license"
        setPhoto={setPhoto}
        existingUrl={existingUrl}
        demoMode={demoMode}
        onExtract={(r) => {
          setPersonal({
            ...personal,
            firstName: personal.firstName || r.fields.firstName || '',
            lastName: personal.lastName || r.fields.lastName || '',
            dateOfBirth: personal.dateOfBirth || r.fields.dateOfBirth || '',
            address: personal.address || r.fields.address || '',
          });
          if (r.fields.expirationDate) setLicenseExp(r.fields.expirationDate);
        }}
      />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Name on ID" value={`${personal.firstName} ${personal.lastName}`.trim()} onChange={() => {}} />
        <Field label="ID expiration" type="date" value={licenseExp} onChange={setLicenseExp} />
      </div>
      <p className="text-xs text-ink-muted">
        Confirm your name and address on the previous step match your ID. Edit them there if needed.
      </p>
    </div>
  );
}

function BadgeStep({
  badgeExp,
  setBadgeExp,
  photo,
  setPhoto,
  existingUrl,
}: {
  badgeExp: string;
  setBadgeExp: (v: string) => void;
  photo: CapturedPhoto | null;
  setPhoto: (p: CapturedPhoto | null) => void;
  existingUrl?: string;
}) {
  return (
    <div className="card p-6 space-y-5">
      <p className="text-sm text-ink-muted">
        You need a Long Beach (KLGB) badge to reach {TRACKED_REG} on your own. Without one, someone holding a
        current badge has to escort you onto the ramp <strong>every single time</strong> you go to the
        airplane. That works, and it is not the way you want to fly. Lease §9.3 puts getting a badge,
        and the cost of it, on you.
      </p>
      <p className="text-sm text-ink-muted">
        You can skip this step and add it from the Documents tab once the badging process is done.
      </p>
      <PhotoCapture subject="KLGB airport badge" onChange={setPhoto} initialPreview={existingUrl} />
      {(photo || existingUrl) && <p className="text-xs text-emerald-700">Photo attached.</p>}
      <Field className="sm:max-w-xs" label="Badge expiration (if shown)" type="date" value={badgeExp} onChange={setBadgeExp} />
    </div>
  );
}

function DisclosuresStep({
  disclosures,
  setDisclosures,
}: {
  disclosures: Disclosures;
  setDisclosures: (d: Disclosures) => void;
}) {
  const keys = Object.keys(disclosures) as Array<keyof Disclosures>;
  return (
    <div className="card p-6 space-y-6">
      <p className="text-sm text-ink-muted">
        Required for insurance. Answer honestly — a “yes” doesn’t disqualify you, but undisclosed
        history can.
      </p>
      {keys.map((k) => {
        const item = disclosures[k];
        return (
          <div key={k} className="border-b border-slate-100 pb-5 last:border-0 last:pb-0">
            <p className="text-sm font-medium text-navy-900">{DISCLOSURE_LABELS[k]}</p>
            <div className="mt-2 flex gap-2">
              {(['no', 'yes'] as const).map((ans) => (
                <button
                  key={ans}
                  type="button"
                  onClick={() => setDisclosures({ ...disclosures, [k]: { ...item, answer: ans } })}
                  className={`rounded-md border px-4 py-1.5 text-sm capitalize ${
                    item.answer === ans
                      ? 'border-brand bg-brand text-white'
                      : 'border-slate-300 text-ink-muted'
                  }`}
                >
                  {ans}
                </button>
              ))}
            </div>
            {item.answer === 'yes' && (
              <textarea
                className="input-textarea mt-3"
                placeholder="Please explain (dates, circumstances, outcome)…"
                value={item.explanation}
                onChange={(e) => setDisclosures({ ...disclosures, [k]: { ...item, explanation: e.target.value } })}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

// Renter's-insurance provider: Wings Insurance (the aircraft's own broker — their
// renters link tags the quote to AJM so their team can help right away).
const INSURANCE_PROVIDERS: { name: string; contact?: string; phone?: string; url?: string }[] = [
  { name: 'Wings Insurance', contact: "The aircraft's insurance broker — quotes renter's policies online in minutes.", url: 'https://www.wingsinsurance.com/renters-jr/' },
];

function InsuranceStep({
  insurance,
  setInsurance,
  setPhoto,
  existingUrl,
  demoMode,
}: {
  insurance: InsuranceInfo;
  setInsurance: (i: InsuranceInfo) => void;
  setPhoto: (p: CapturedPhoto | null) => void;
  existingUrl?: string;
  demoMode: boolean;
}) {
  const [insuredValue, setInsuredValue] = useState(122000);
  useEffect(() => {
    if (demoMode || !db) return;
    getDocs(collection(db, 'aircraft'))
      .then((s) => {
        const v = s.docs[0]?.data()?.insuredValue;
        if (typeof v === 'number' && v > 0) setInsuredValue(v);
      })
      .catch(() => {});
  }, [demoMode]);

  return (
    <div className="card p-6 space-y-5">
      <p className="text-sm text-ink-muted">
        This is the last step. Renter’s / non-owned aircraft insurance is <strong>required before you fly
        solo</strong> — and in any case within <strong>14 days</strong> of activation. It won’t hold up your
        onboarding or your checkout (checkout flights are dual, with an insurance-approved instructor), and a
        policy takes just a few minutes to get online. If you already have a policy, photograph your
        declarations page or insurance card below.
      </p>

      <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
        <div className="text-sm font-semibold text-amber-900">Your policy needs at least:</div>
        <ul className="mt-1.5 space-y-1 text-sm text-amber-900">
          <li>
            • <strong>$1,000,000</strong> liability (combined single limit)
          </li>
          <li>
            • <strong>{fmtUSD(insuredValue)}</strong> aircraft damage (non-owned hull) coverage
          </li>
        </ul>
        <p className="mt-2 text-xs leading-snug text-amber-900">
          That second number is the insured value of {TRACKED_REG}. If you damage the airplane you are responsible for
          the <strong>cost of repair up to that value</strong> — not just AJM’s deductible. Anything you carry
          short of it is yours personally. Lease §11.2.
        </p>
      </div>

      {/* Exactly how the policy must be written */}
      <div className="rounded-xl bg-sky-50 p-4 ring-1 ring-navy-900/10">
        <div className="text-sm font-semibold text-navy-900">List BOTH of these as additional insured on your policy:</div>
        <ul className="mt-1.5 space-y-1 text-sm text-ink">
          <li>• <strong>AJM Aviation Ventures, LLC</strong></li>
          <li>• <strong>{OWNER_LEGAL}</strong> (individually)</li>
        </ul>
        <div className="mt-1.5 text-xs text-ink-muted">101 Main Street, Ste H, Seal Beach, CA 90740</div>
      </div>

      {INSURANCE_PROVIDERS.length > 0 ? (
        <div>
          <div className="text-sm font-medium text-navy-900">Don’t have a policy yet? Get a quick quote:</div>
          <div className="mt-2 grid grid-cols-1 gap-3">
            {INSURANCE_PROVIDERS.map((p) => (
              <div key={p.name} className="rounded-xl border border-slate-200 p-3">
                <div className="font-medium text-navy-900">{p.name}</div>
                {p.contact && <div className="text-xs text-ink-muted">{p.contact}</div>}
                <div className="mt-2 flex flex-col gap-2">
                  {p.phone && (
                    <a
                      href={`tel:${p.phone.replace(/[^0-9]/g, '')}`}
                      className="inline-flex items-center justify-center gap-2 rounded-lg bg-brand px-3 py-2 text-sm font-semibold text-white transition hover:bg-brand-600"
                    >
                      <Phone size={15} /> Call {p.phone}
                    </a>
                  )}
                  {p.url && (
                    <a
                      href={p.url}
                      target="_blank"
                      rel="noopener"
                      className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-sky-50 px-3 py-2 text-sm font-medium text-navy-700 transition hover:bg-sky-100"
                    >
                      <ExternalLink size={14} /> Get a quote online
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <p className="text-xs text-ink-muted">
          Don’t have a policy yet? No problem — finish onboarding now and add it within 14 days.
          You just can’t fly solo until it’s on file.
        </p>
      )}

      <CaptureBlock
        subject="insurance card"
        docType="renters_insurance"
        setPhoto={setPhoto}
        existingUrl={existingUrl}
        demoMode={demoMode}
        onExtract={(r) =>
          setInsurance({
            ...insurance,
            carrier: r.fields.carrier || insurance.carrier,
            policyNumber: r.fields.policyNumber || insurance.policyNumber,
            liabilityLimit: r.fields.liabilityLimit || insurance.liabilityLimit,
            expirationDate: r.fields.expirationDate || insurance.expirationDate,
          })
        }
      />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Insurance carrier" value={insurance.carrier} onChange={(v) => setInsurance({ ...insurance, carrier: v })} />
        <Field label="Policy number" value={insurance.policyNumber} onChange={(v) => setInsurance({ ...insurance, policyNumber: v })} />
        <Field
          label="Liability limit (USD)"
          type="text"
          placeholder="$1,000,000"
          value={insurance.liabilityLimit ? `$${insurance.liabilityLimit.toLocaleString()}` : ''}
          onChange={(v) => setInsurance({ ...insurance, liabilityLimit: Number(v.replace(/[^0-9]/g, '')) || 0 })}
        />
        <Field label="Policy expiration" type="date" value={insurance.expirationDate} onChange={(v) => setInsurance({ ...insurance, expirationDate: v })} />
      </div>
    </div>
  );
}

function ReviewStep({
  personal,
  pilot,
  photoCount,
}: {
  personal: PersonalInfo;
  pilot: PilotInfo;
  photoCount: number;
}) {
  const rows: Array<[string, string]> = [
    ['Name', `${personal.firstName} ${personal.lastName}`.trim() || '—'],
    ['Certificate', `${pilot.certificateType || '—'} ${pilot.certificateNumber || ''}`.trim()],
    ['Medical', pilot.medicalClass ? `${pilot.medicalClass} · exp ${pilot.medicalExpiration || '—'}` : '—'],
    ['Total hours', String(pilot.totalHours || 0)],
    ['Planned lease length', LEASE_DURATION_LABEL[pilot.intendedLeaseDuration] ?? '—'],
    ['Documents attached', String(photoCount)],
  ];
  return (
    <div className="card overflow-hidden">
      <div className="bg-sky-50 px-5 py-3 text-sm font-medium text-navy-900">
        Here’s what we’ll send for review
      </div>
      <dl className="divide-y divide-slate-100">
        {rows.map(([k, v]) => (
          <div key={k} className="flex justify-between gap-4 px-5 py-3 text-sm">
            <dt className="text-ink-muted">{k}</dt>
            <dd className="text-right font-medium text-navy-900">{v}</dd>
          </div>
        ))}
      </dl>
      <p className="px-5 py-4 text-xs text-ink-muted">
        By submitting, you confirm the information and documents are accurate and current. AJM
        Aviation will verify everything before activating your account.
      </p>
    </div>
  );
}
